import { useTranslation } from 'react-i18next';

import { useTenantFormats } from '../shell/formats';
import { Chip, ChipGroup, Input } from '../ui';
import { periodPresets, type PeriodPreset } from './api';
import type { ReportFrame } from './frame';

/**
 * The row under every report's header: six presets, the custom range when it is current, and
 * the dates the numbers on screen were actually computed over.
 *
 * The right-hand line is not a restatement of the chip somebody clicked. A label is a
 * request; the dates are what the server resolved it to, in the firm's timezone. While a
 * report is loading there is deliberately nothing to say — showing the previous report's
 * dates over the next report's numbers is the one thing this row must never do — so the word
 * for working stands in its place and the row does not change height.
 */
export function PeriodRow({ frame }: { frame: ReportFrame }) {
  const { t } = useTranslation();
  const formats = useTenantFormats();

  const { period, setPeriod, busy, resolved, seed } = frame;

  /** Switching preset. Custom opens on the last dates the server resolved. */
  const choose = (preset: PeriodPreset) => {
    if (preset === period.preset) return;

    setPeriod(
      preset === 'custom' && seed !== null
        ? { preset, from: seed.from, to: seed.to }
        : { ...period, preset },
    );
  };

  return (
    <div className="flex items-center justify-between gap-4 flex-wrap">
      <div className="flex items-center gap-3 flex-wrap">
        <ChipGroup label={t('reports.period.label')}>
          {periodPresets.map((preset) => (
            <Chip key={preset} current={preset === period.preset} onClick={() => choose(preset)}>
              {t(`reports.period.${preset}`)}
            </Chip>
          ))}
        </ChipGroup>

        {period.preset === 'custom' && (
          <div className="flex items-center gap-2 flex-wrap">
            <label className="flex items-center gap-1.5 text-secondary text-ink-3">
              {t('reports.period.from')}
              <Input
                type="date"
                className="w-[150px]"
                value={period.from}
                onChange={(event) => setPeriod({ ...period, from: event.target.value })}
              />
            </label>

            <label className="flex items-center gap-1.5 text-secondary text-ink-3">
              {t('reports.period.to')}
              <Input
                type="date"
                className="w-[150px]"
                value={period.to}
                onChange={(event) => setPeriod({ ...period, to: event.target.value })}
              />
            </label>
          </div>
        )}
      </div>

      <span className="text-secondary text-ink-3" aria-live="polite">
        {busy
          ? t('reports.loading')
          : resolved !== null &&
            t('reports.period.showing', {
              from: formats.dayLong(resolved.from),
              to: formats.dayLong(resolved.to),
              zone: formats.timeZone,
            })}
      </span>
    </div>
  );
}
